// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, UserPlus, Trash2, Loader2, Users, CheckCircle, Pencil, X, Save } from 'lucide-react';
import { toast } from 'sonner';

interface RosterPlayer {
  id: string;
  roster_member_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  cell_phone: string | null;
  jersey_number: string | null;
  position: string | null;
  grad_year: number | null;
}

interface AvailablePlayer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  cell_phone: string | null;
  jersey_number: string | null;
  position: string | null;
  grad_year: number | null;
}

interface EditPlayerData {
  first_name: string;
  last_name: string;
  email: string;
  cell_phone: string;
  jersey_number: string;
  position: string;
  grad_year: string;
}

interface AdminRosterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  teamId: string;
  teamName: string;
  onRosterUpdated: () => void;
}

export const AdminRosterModal: React.FC<AdminRosterModalProps> = ({
  open,
  onOpenChange,
  eventId,
  teamId,
  teamName,
  onRosterUpdated,
}) => {
  const [rosterPlayers, setRosterPlayers] = useState<RosterPlayer[]>([]);
  const [availablePlayers, setAvailablePlayers] = useState<AvailablePlayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [editingPlayer, setEditingPlayer] = useState<RosterPlayer | null>(null);
  const [editData, setEditData] = useState<EditPlayerData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && eventId && teamId) {
      fetchRosterData();
    }
  }, [open, eventId, teamId]);

  const fetchRosterData = async () => {
    setIsLoading(true);
    try {
      // Fetch current roster for this event/team
      const { data: eventRoster, error: rosterError } = await supabase
        .from('event_team_rosters')
        .select('id, roster_member_id')
        .eq('event_id', eventId)
        .eq('team_id', teamId);

      if (rosterError) throw rosterError;

      const rosterMemberIds = (eventRoster || []).map(r => r.roster_member_id);

      // Fetch player details for roster members
      if (rosterMemberIds.length > 0) {
        const { data: players, error: playersError } = await supabase
          .from('coach_team_rosters')
          .select('id, first_name, last_name, email, cell_phone, jersey_number, position, grad_year')
          .in('id', rosterMemberIds);

        if (playersError) throw playersError;

        const rosterWithDetails: RosterPlayer[] = (eventRoster || []).map(r => {
          const player = players?.find(p => p.id === r.roster_member_id);
          return {
            id: r.id,
            roster_member_id: r.roster_member_id,
            first_name: player?.first_name || null,
            last_name: player?.last_name || null,
            email: player?.email || null,
            cell_phone: player?.cell_phone || null,
            jersey_number: player?.jersey_number || null,
            position: player?.position || null,
            grad_year: player?.grad_year || null,
          };
        });

        setRosterPlayers(rosterWithDetails);
      } else {
        setRosterPlayers([]);
      }

      // Fetch all team members not in roster
      const { data: allMembers, error: membersError } = await supabase
        .from('coach_team_rosters')
        .select('id, first_name, last_name, email, cell_phone, jersey_number, position, grad_year')
        .eq('team_id', teamId);

      if (membersError) throw membersError;

      const available = (allMembers || []).filter(
        m => !rosterMemberIds.includes(m.id)
      );
      setAvailablePlayers(available);
    } catch (error) {
      console.error('Error fetching roster:', error);
      toast.error('Failed to load roster data');
    } finally {
      setIsLoading(false);
    }
  };

  const addPlayerToRoster = async (memberId: string) => {
    setIsAdding(memberId);
    try {
      const { error } = await supabase
        .from('event_team_rosters')
        .insert({
          event_id: eventId,
          team_id: teamId,
          roster_member_id: memberId,
        });

      if (error) throw error;

      toast.success('Player added to roster');
      await fetchRosterData();
      onRosterUpdated();
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player');
    } finally {
      setIsAdding(null);
    }
  };

  const removePlayerFromRoster = async (eventRosterId: string) => {
    setIsRemoving(eventRosterId);
    try {
      const { error } = await supabase
        .from('event_team_rosters')
        .delete()
        .eq('id', eventRosterId);

      if (error) throw error;

      toast.success('Player removed from roster');
      await fetchRosterData();
      onRosterUpdated();
    } catch (error) {
      console.error('Error removing player:', error);
      toast.error('Failed to remove player');
    } finally {
      setIsRemoving(null);
    }
  };

  const startEditing = (player: RosterPlayer) => {
    setEditingPlayer(player);
    setEditData({
      first_name: player.first_name || '',
      last_name: player.last_name || '',
      email: player.email || '',
      cell_phone: player.cell_phone || '',
      jersey_number: player.jersey_number || '',
      position: player.position || '',
      grad_year: player.grad_year?.toString() || '',
    });
  };

  const cancelEditing = () => {
    setEditingPlayer(null);
    setEditData(null);
  };

  const savePlayer = async () => {
    if (!editingPlayer || !editData) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('coach_team_rosters')
        .update({
          first_name: editData.first_name || null,
          last_name: editData.last_name || null,
          email: editData.email || null,
          cell_phone: editData.cell_phone || null,
          jersey_number: editData.jersey_number || null,
          position: editData.position || null,
          grad_year: editData.grad_year ? parseInt(editData.grad_year) : null,
        })
        .eq('id', editingPlayer.roster_member_id);

      if (error) throw error;

      toast.success('Player updated successfully');
      cancelEditing();
      await fetchRosterData();
    } catch (error) {
      console.error('Error updating player:', error);
      toast.error('Failed to update player');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredAvailable = availablePlayers.filter(p => {
    const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {teamName} - Event Roster
            </DialogTitle>
            <DialogDescription>
              Manage which players are on this team's roster for this event
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex-1 overflow-auto space-y-6">
              {/* Current Roster */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                  Current Event Roster ({rosterPlayers.length} players)
                </h3>
                {rosterPlayers.length === 0 ? (
                  <div className="text-center py-6 bg-muted/50 rounded-lg">
                    <p className="text-muted-foreground">No players on roster yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Grad Year</TableHead>
                        <TableHead className="w-[120px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rosterPlayers.map((player) => (
                        <TableRow key={player.id}>
                          <TableCell>{player.jersey_number || '-'}</TableCell>
                          <TableCell className="font-medium">
                            {player.first_name} {player.last_name}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {player.email || '-'}
                          </TableCell>
                          <TableCell>{player.position || '-'}</TableCell>
                          <TableCell>{player.grad_year || '-'}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditing(player)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removePlayerFromRoster(player.id)}
                                disabled={isRemoving === player.id}
                                className="text-destructive hover:text-destructive"
                              >
                                {isRemoving === player.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Available Players */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Available Team Members ({availablePlayers.length})
                </h3>
                
                {availablePlayers.length > 0 && (
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search players..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                )}

                {availablePlayers.length === 0 ? (
                  <div className="text-center py-6 bg-muted/50 rounded-lg">
                    <p className="text-muted-foreground">All team members are on the roster</p>
                  </div>
                ) : filteredAvailable.length === 0 ? (
                  <div className="text-center py-6 bg-muted/50 rounded-lg">
                    <p className="text-muted-foreground">No players match your search</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Grad Year</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAvailable.map((player) => (
                        <TableRow key={player.id}>
                          <TableCell>{player.jersey_number || '-'}</TableCell>
                          <TableCell className="font-medium">
                            {player.first_name} {player.last_name}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {player.email || '-'}
                          </TableCell>
                          <TableCell>{player.position || '-'}</TableCell>
                          <TableCell>{player.grad_year || '-'}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => addPlayerToRoster(player.id)}
                              disabled={isAdding === player.id}
                              className="text-primary hover:text-primary"
                            >
                              {isAdding === player.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserPlus className="h-4 w-4" />
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Player Dialog */}
      <Dialog open={!!editingPlayer} onOpenChange={(open) => !open && cancelEditing()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Player</DialogTitle>
            <DialogDescription>
              Update player information for this roster entry
            </DialogDescription>
          </DialogHeader>
          
          {editData && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={editData.first_name}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={editData.last_name}
                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cell_phone">Cell Phone</Label>
                <Input
                  id="cell_phone"
                  value={editData.cell_phone}
                  onChange={(e) => setEditData({ ...editData, cell_phone: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="jersey_number">Jersey #</Label>
                  <Input
                    id="jersey_number"
                    value={editData.jersey_number}
                    onChange={(e) => setEditData({ ...editData, jersey_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    value={editData.position}
                    onChange={(e) => setEditData({ ...editData, position: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="grad_year">Grad Year</Label>
                  <Input
                    id="grad_year"
                    type="number"
                    value={editData.grad_year}
                    onChange={(e) => setEditData({ ...editData, grad_year: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={cancelEditing} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={savePlayer} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
